import React, { useCallback, useEffect, useState } from 'react';
import './TimesheetReview2Page.css';
import { AlertCircle, CheckCircle, Unlock } from 'lucide-react';
import ConfirmModal, { ConfirmModalProps } from '../components/ConfirmModal';
import PaidOverrideDetail from '../components/PaidOverrideDetail';
import { isPaidNotExhaustedError, refusalMessageOf, timeOffCountsTowardHoliday } from '../util/timeOffUnpaid';
import { useSiteContext } from '../SiteContext';
import { TimesheetType, PunchType, TimeEntryType } from '../interfaces/TimesheetInterface';
import { Day, Employee, EmployeeSickClaim } from '../interfaces/EmployeeInterface';
import { Supervisor, SupervisorsEmployee } from '../interfaces/SupervisorInterface';
import { TimeOffRequestSummary, TimeOffRequestDetail, TimeOffRequestStatus } from '../interfaces/TimeOffRequest';
import { HolidayCalendarEntry, HolidayTenureVerdict, TenureStatus, WorkedSources } from '../interfaces/TimeOff';
import { PayPeriod } from '../interfaces/PayPeriodInterface';
import {
  DriveTimeEntry,
  getSupervisorsByStatus,
  getTimesheetForReview,
  getTimesheetForAnyStatus,
  getDriveTimeEntries,
  getEmployeeTimeOffForWeek,
  getEmployeeHolidays,
  approveToPayroll,
} from '../api/timesheetReview';
import { approveTimeOffRequest, denyTimeOffRequest, getTimeOffRequestById } from '../api/timeOffRequests';
import { unlockTimesheet, updateSickClaimAmount } from '../api/timesheet';
import { applyOffsetToUTC, getShortDate } from '../util/DateAndTimeUtil';
import PageWrapper from '../components/PageWrapper';
import Colors from '../styles/Colors.json';
import EmployeeTimesheetReviewConsole, { bucketLabel, type StatusKey as ConsoleStatusKey } from './EmployeeTimesheetReviewConsole';
import config from '../configLoader';
import { can } from '../util/adminPermissions';
import WeekPickerNav from '../components/WeekPickerNav';

// ── Types ─────────────────────────────────────────────────────────────────────

type View = 'buckets' | 'supervisors' | 'employees' | 'review';

interface EmployeeAlertInfo {
  hasAlerts: boolean;
  hasPendingTimeOff: boolean;
  loading: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_KEYS = ['unsubmitted', 'submitted', 'supervisorApproved', 'payrollApproved'] as const;
type StatusKey = typeof STATUS_KEYS[number];

const STATUS_TO_TYPE: Record<StatusKey, TimesheetType> = {
  unsubmitted: TimesheetType.Unsubmitted,
  submitted: TimesheetType.Submitted,
  supervisorApproved: TimesheetType.SuperApproved,
  payrollApproved: TimesheetType.PayApproved,
};

// Shared stage vocabulary (translator keys with the shipped literals as defaults) —
// carries the global "Supervisor Approved" → "Payroll Pending" rename.
const BUCKET_LABELS: Record<StatusKey, string> = {
  unsubmitted: bucketLabel('unsubmitted'),
  submitted: bucketLabel('submitted'),
  supervisorApproved: bucketLabel('supervisorApproved'),
  payrollApproved: bucketLabel('payrollApproved'),
};

// ── New interfaces ─────────────────────────────────────────────────────────────

interface TimeOffDayEntry {
  requestID: number;
  category: string;
  hours: number;
  status: TimeOffRequestStatus;
  /** Unpaid Time (TAS-586). Missing = paid. */
  isUnpaid?: boolean;
  /** Whether this day counts as "worked" for the holiday rule. Missing = true. */
  countsForHolidayEligibility?: boolean;
}

type EligibilityStatus = 'pass' | 'fail' | 'outside' | 'na';

interface HolidayEligibility {
  /** Days-employed leg, straight from the server verdict — tenure depends on the
   *  hire date and the holiday date only, never on the loaded week.
   *  'na' = no waiting period configured, so the row is not rendered. */
  tenureStatus: TenureStatus;
  /** Fully-rendered tenure label, e.g. "Hired Mar 8, 2024 — eligible from Sep 4, 2024". */
  tenureNote: string;
  beforeStatus: EligibilityStatus;
  beforeDate: string;
  /** Suffix appended after the date in the rendered label, e.g. " — prior week". */
  beforeNote: string;
  afterStatus: EligibilityStatus;
  afterDate: string;
  afterNote: string;
}

// ── New helpers ────────────────────────────────────────────────────────────────

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/** "Mar 8, 2024" — hire and eligibility dates are usually years away from the
 *  loaded week, so they always carry the year. */
function fmtLongDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00Z');
  return `${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/** The tenure row's label, built purely from the server verdict. */
function describeTenure(tenure: HolidayTenureVerdict | null | undefined): string {
  if (!tenure || tenure.status === 'na') return '';
  if (tenure.status === 'unknown' || !tenure.hireDate) {
    return 'Hire date unknown — eligibility not checked';
  }
  const hired = `Hired ${fmtLongDate(tenure.hireDate)}`;
  const eligible = tenure.eligibleOn ? ` — eligible from ${fmtLongDate(tenure.eligibleOn)}` : '';
  // The waiting period is spelled out only on a failure, where it answers "why not?".
  const days = tenure.status === 'fail' && tenure.eligibilityDays != null
    ? ` (${tenure.eligibilityDays} days)`
    : '';
  return `${hired}${eligible}${days}`;
}

function addDays(dateStr: string, n: number): string {
  const d = new Date(dateStr + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().split('T')[0];
}

function buildTimeOffDetailMap(details: TimeOffRequestDetail[]): Record<string, TimeOffDayEntry[]> {
  const map: Record<string, TimeOffDayEntry[]> = {};
  details.forEach((req) => {
    (req.details ?? []).forEach((d) => {
      const key = d.requestDate.split('T')[0];
      if (!map[key]) map[key] = [];
      map[key].push({
        requestID: req.timeOffRequestID,
        category: req.categoryName,
        hours: d.hours,
        status: req.status,
        // Unpaid Time (TAS-586) — per request, so every day of it carries the flags.
        isUnpaid: req.isUnpaid,
        countsForHolidayEligibility: req.countsForHolidayEligibility,
      });
    });
  });
  return map;
}

/** Short descriptor for how the adjacent day was satisfied, for the label suffix.
 *  Plain hourly work needs no descriptor — the label already reads "Worked". */
function describeSource(source: WorkedSources | null | undefined): string {
  if (!source) return '';
  if (source.hourly) return '';
  if (source.piece || source.taskClaim) return 'piece work';
  if (source.timeOff) return 'time off';
  return '';
}

function checkHolidayEligibility(
  holiday: HolidayCalendarEntry,
  days: Day[],
  timeOffDetailMap: Record<string, TimeOffDayEntry[]>,
  payPeriodStart: string,
  payPeriodEnd: string
): HolidayEligibility {
  // In-period check stays live so unsaved hour edits are reflected immediately.
  // Aligned with the server/export rule: hourly or piece punches, a task claim,
  // or an APPROVED *or PENDING* time-off day all count as "worked" — time off only
  // when its category counts for holiday eligibility (TAS-586; missing = counts).
  const workedOnDate = (date: string): boolean => {
    const day = days.find((d) => d.date.substring(0, 10) === date);
    if (day && (day.totalWorkMinutes ?? 0) > 0) return true;
    if (day && (day.projectEmployeeTasks ?? []).some((p) => (p.employeeTasks?.length ?? 0) > 0)) return true;
    return timeOffCountsTowardHoliday(timeOffDetailMap[date] ?? []);
  };

  const isInPeriod = (date: string): boolean =>
    date >= payPeriodStart && date <= payPeriodEnd;

  const beforeDate = holiday.requiredWorkDayBeforeDate ?? addDays(holiday.holidayDate, -1);
  const afterDate = holiday.requiredWorkDayAfterDate ?? addDays(holiday.holidayDate, 1);

  // Outside the loaded week we cannot see the days, so we trust the server
  // verdict. No verdict — older API, or a probe that failed — degrades to '?'.
  const verdict = holiday.eligibility;
  const outsideLeg = (
    status: 'pass' | 'fail' | 'na' | undefined,
    source: WorkedSources | null | undefined,
    weekWord: string
  ): { status: EligibilityStatus; note: string } => {
    if (status !== 'pass' && status !== 'fail') {
      return { status: 'outside', note: ' — could not check (outside period)' };
    }
    const descriptor = status === 'pass' ? describeSource(source) : '';
    return {
      status,
      note: descriptor ? ` — ${descriptor} (${weekWord})` : ` — ${weekWord}`,
    };
  };

  const before: { status: EligibilityStatus; note: string } = !holiday.requireWorkDayBefore
    ? { status: 'na', note: '' }
    : !isInPeriod(beforeDate)
    ? outsideLeg(verdict?.beforeStatus, verdict?.beforeSource, 'prior week')
    : { status: workedOnDate(beforeDate) ? 'pass' : 'fail', note: '' };

  const after: { status: EligibilityStatus; note: string } = !holiday.requireWorkDayAfter
    ? { status: 'na', note: '' }
    : !isInPeriod(afterDate)
    ? outsideLeg(verdict?.afterStatus, verdict?.afterSource, 'next week')
    : { status: workedOnDate(afterDate) ? 'pass' : 'fail', note: '' };

  // Tenure is never recomputed locally — an older API without the verdict
  // degrades to 'na', hiding the row and blocking nothing.
  const tenure = verdict?.tenure;

  return {
    tenureStatus: tenure?.status ?? 'na',
    tenureNote: describeTenure(tenure),
    beforeStatus: before.status,
    beforeDate,
    beforeNote: before.note,
    afterStatus: after.status,
    afterDate,
    afterNote: after.note,
  };
}

function formatDecimalHours(minutes: number): string {
  return (minutes / 60).toFixed(1);
}

function formatDollar(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getWeekLabel(payPeriod: PayPeriod | undefined): string {
  if (!payPeriod) return '';
  const start = new Date(payPeriod.StartDate);
  const end = new Date(payPeriod.EndDate);
  const fmt = (d: Date) => `${d.getUTCMonth() + 1}/${d.getUTCDate().toString().padStart(2, '0')}`;
  return `${fmt(start)}–${fmt(end)}`;
}

function getDayLabel(date: string): string {
  const d = new Date(date);
  d.setUTCHours(12);
  return d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
}

function isoDateOnly(date: Date): string {
  return date.toISOString().split('T')[0];
}

function getTimeGapEntries(day: Day): Set<string> {
  const gapTimes = new Set<string>();
  const allEntries: Array<{ inDateTime: string; outDateTime: string | null; identifier: string }> = [];

  day.timeEntries.forEach((entry, i) =>
    allEntries.push({ inDateTime: entry.inDateTime, outDateTime: entry.outDateTime || null, identifier: `te-${i}` })
  );
  if (day.hourlyProjects) {
    day.hourlyProjects.forEach((p, pi) =>
      p.timeEntriesHourly?.forEach((e, ei) =>
        allEntries.push({ inDateTime: e.inDateTime, outDateTime: e.outDateTime || null, identifier: `hp-${pi}-${ei}` })
      )
    );
  }

  if (allEntries.length <= 1) return gapTimes;
  allEntries.sort((a, b) => new Date(a.inDateTime).getTime() - new Date(b.inDateTime).getTime());

  for (let i = 0; i < allEntries.length - 1; i++) {
    const curr = allEntries[i];
    const next = allEntries[i + 1];
    if (!curr.outDateTime) continue;
    if (new Date(curr.outDateTime).getTime() !== new Date(next.inDateTime).getTime()) {
      gapTimes.add(`${curr.identifier}-out`);
      gapTimes.add(`${next.identifier}-in`);
    }
  }
  return gapTimes;
}

function computeDayAlerts(day: Day) {
  return {
    missingPunch: day.timeEntries.some((e) => e.type === TimeEntryType.Regular && !e.outDateTime),
    mealViolation: day.fiveHourLunchException || day.fullLunchException || day.noLunchException,
    editedTime: day.timeEntries.some(
      (e) => e.punchTypeIn === PunchType.ManualEdit || e.punchTypeOut === PunchType.ManualEdit
    ),
  };
}

function computeWeekAlerts(days: Day[]) {
  let missingPunch = 0, mealViolation = 0, editedTime = 0;
  days.forEach((d) => {
    const a = computeDayAlerts(d);
    if (a.missingPunch) missingPunch++;
    if (a.mealViolation) mealViolation++;
    if (a.editedTime) editedTime++;
  });
  return { missingPunch, mealViolation, editedTime };
}

// ── Component ─────────────────────────────────────────────────────────────────

const TimesheetReview2Page: React.FC = () => {
  const { userID, userRole, payPeriods, week } = useSiteContext();

  // ── Navigation state
  const [view, setView] = useState<View>('buckets');
  const [selectedStatus, setSelectedStatus] = useState<StatusKey | null>(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState<Supervisor | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<SupervisorsEmployee | null>(null);

  // ── Week navigation (local)
  const [reviewWeekIdx, setReviewWeekIdx] = useState<number>(week);
  const currentPayPeriod: PayPeriod | undefined = payPeriods[reviewWeekIdx];
  const currentPayPeriodID: number = currentPayPeriod?.PayPeriodID ?? 0;

  // ── Bucket data: supervisor lists keyed by status
  const [allSupervisorData, setAllSupervisorData] = useState<Record<StatusKey, Supervisor[]>>({
    unsubmitted: [], submitted: [], supervisorApproved: [], payrollApproved: [],
  });
  const [bucketsLoading, setBucketsLoading] = useState(false);
  const [bucketsError, setBucketsError] = useState<string | null>(null);

  // ── Employee alert badges (lazily computed per employee)
  const [employeeAlerts, setEmployeeAlerts] = useState<Record<number, EmployeeAlertInfo>>({});

  // ── Review state
  const [timesheetData, setTimesheetData] = useState<Employee | null>(null);
  const [driveTimeEntries, setDriveTimeEntries] = useState<DriveTimeEntry[]>([]);
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequestSummary[]>([]);
  const [timeOffDetailMap, setTimeOffDetailMap] = useState<Record<string, TimeOffDayEntry[]>>({});
  const [holidays, setHolidays] = useState<HolidayCalendarEntry[]>([]);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [noTimesheetForWeek, setNoTimesheetForWeek] = useState(false);
  // The stage the loaded timesheet is really in, as inferred from its approval
  // rows. Typed on the console's full StatusKey rather than this page's four tabs:
  // the inference can answer `managerPending` on a manager-approval tenant, and
  // this page only ever forwards the value to the console.
  const [actualStatus, setActualStatus] = useState<ConsoleStatusKey | null>(null);

  // ── Approve / sick rate modal
  const [sickRateModalOpen, setSickRateModalOpen] = useState(false);
  const [sickRateInput, setSickRateInput] = useState('');
  const [approveLoading, setApproveLoading] = useState(false);

  // ── Confirm / result modal (replaces window.confirm + alert)
  type CMConfig = Omit<ConfirmModalProps, 'onClose'> & { onDismiss?: () => void };
  const [cmConfig, setCMConfig] = useState<CMConfig | null>(null);
  const dismissCM = () => {
    if (cmConfig?.onDismiss) cmConfig.onDismiss();
    setCMConfig(null);
  };

  const showError = (title: string, subtitle?: string) => {
    setCMConfig({ variant: 'error', icon: <AlertCircle size={22} />, title, subtitle });
  };

  // ── Load all bucket data ───────────────────────────────────────────────────

  const loadAllBuckets = useCallback(async () => {
    if (!currentPayPeriodID) return;
    setBucketsLoading(true);
    setBucketsError(null);
    try {
      const [unsubmitted, submitted, supervisorApproved, payrollApproved] = await Promise.all([
        getSupervisorsByStatus(currentPayPeriodID, TimesheetType.Unsubmitted),
        getSupervisorsByStatus(currentPayPeriodID, TimesheetType.Submitted),
        getSupervisorsByStatus(currentPayPeriodID, TimesheetType.SuperApproved),
        getSupervisorsByStatus(currentPayPeriodID, TimesheetType.PayApproved),
      ]);
      setAllSupervisorData({ unsubmitted, submitted, supervisorApproved, payrollApproved });
    } catch {
      setBucketsError('Failed to load timesheet data.');
    } finally {
      setBucketsLoading(false);
    }
  }, [currentPayPeriodID]);

  useEffect(() => {
    loadAllBuckets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewWeekIdx]);

  // ── Load alert badges for employees when employee list is shown ────────────

  useEffect(() => {
    if (view !== 'employees' || !selectedSupervisor || !currentPayPeriodID || !selectedStatus) return;

    const employees = selectedSupervisor.employees ?? [];
    const type = STATUS_TO_TYPE[selectedStatus];

    // Seed loading state
    const seed: Record<number, EmployeeAlertInfo> = {};
    employees.forEach((e) => { seed[e.employeeID] = { hasAlerts: false, hasPendingTimeOff: false, loading: true }; });
    setEmployeeAlerts(seed);

    // Load in parallel — update each as it completes
    employees.forEach(async (emp) => {
      try {
        const [ts, pp] = await Promise.all([
          getTimesheetForReview(emp.employeeID, currentPayPeriodID, type),
          currentPayPeriod
            ? getEmployeeTimeOffForWeek(
                emp.employeeID,
                isoDateOnly(new Date(currentPayPeriod.StartDate)),
                isoDateOnly(new Date(currentPayPeriod.EndDate))
              )
            : Promise.resolve([]),
        ]);
        const alerts = computeWeekAlerts(ts?.days ?? []);
        const hasAlerts = alerts.missingPunch > 0 || alerts.mealViolation > 0;
        const hasPendingTimeOff = (pp as TimeOffRequestSummary[]).some((r) => r.status === 'PENDING');
        setEmployeeAlerts((prev) => ({
          ...prev,
          [emp.employeeID]: { hasAlerts, hasPendingTimeOff, loading: false },
        }));
      } catch {
        setEmployeeAlerts((prev) => ({
          ...prev,
          [emp.employeeID]: { hasAlerts: false, hasPendingTimeOff: false, loading: false },
        }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedSupervisor, currentPayPeriodID]);

  // ── Load review data ───────────────────────────────────────────────────────

  const loadReview = useCallback(
    async (emp: SupervisorsEmployee, status: StatusKey, payPeriodID: number) => {
      setReviewLoading(true);
      setReviewError(null);
      setNoTimesheetForWeek(false);
      setActualStatus(null);
      setDriveTimeEntries([]);
      setTimeOffRequests([]);
      setTimeOffDetailMap({});
      setHolidays([]);
      setExpandedDays(new Set());

      try {
        const pp = payPeriods[reviewWeekIdx];
        const startDate = pp ? isoDateOnly(new Date(pp.StartDate)) : '';
        const endDate = pp ? isoDateOnly(new Date(pp.EndDate)) : '';

        const timeOffPromise = pp
          ? getEmployeeTimeOffForWeek(emp.employeeID, startDate, endDate)
          : Promise.resolve<TimeOffRequestSummary[]>([]);
        const holidayPromise = pp
          ? getEmployeeHolidays(emp.employeeID, startDate, endDate)
          : Promise.resolve<HolidayCalendarEntry[]>([]);

        const [tsResult, drive, tor, hols] = await Promise.all([
          getTimesheetForAnyStatus(emp.employeeID, payPeriodID),
          getDriveTimeEntries(emp.employeeID, payPeriodID),
          timeOffPromise,
          holidayPromise,
        ]);

        if (tsResult) {
          setTimesheetData(tsResult.employee);
          setActualStatus(tsResult.statusKey);
        }
        setNoTimesheetForWeek(!tsResult);
        setDriveTimeEntries(drive);
        setTimeOffRequests(tor);
        setHolidays(hols);

        // Fetch per-day breakdown for each time-off request
        if (tor.length > 0) {
          const details = await Promise.all(
            tor.map((r) => getTimeOffRequestById(r.timeOffRequestID).catch(() => null))
          );
          setTimeOffDetailMap(
            buildTimeOffDetailMap(details.filter(Boolean) as TimeOffRequestDetail[])
          );
        }
      } catch {
        setReviewError('Failed to load timesheet data.');
      } finally {
        setReviewLoading(false);
      }
    },
    [payPeriods, reviewWeekIdx]
  );

  // Re-fetch when week changes while in review
  useEffect(() => {
    if (view === 'review' && selectedEmployee && selectedStatus) {
      const pp = payPeriods[reviewWeekIdx];
      if (pp) loadReview(selectedEmployee, selectedStatus, pp.PayPeriodID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewWeekIdx]);

  // ── Navigation handlers ────────────────────────────────────────────────────

  const handleSelectBucket = (key: StatusKey) => {
    setSelectedStatus(key);
    setView('supervisors');
  };

  const handleSelectSupervisor = (sup: Supervisor) => {
    setSelectedSupervisor(sup);
    setEmployeeAlerts({});
    setView('employees');
  };

  const handleSelectEmployee = (emp: SupervisorsEmployee) => {
    setSelectedEmployee(emp);
    setView('review');
    if (selectedStatus) loadReview(emp, selectedStatus, currentPayPeriodID);
  };

  const handleBackToEmployees = () => {
    setView('employees');
    setTimesheetData(null);
    setActualStatus(null);
  };

  const handleBackToSupervisors = () => {
    setView('supervisors');
    setSelectedEmployee(null);
    setEmployeeAlerts({});
  };

  const handleBackToBuckets = () => {
    setView('buckets');
    setSelectedStatus(null);
    setSelectedSupervisor(null);
  };

  // ── Week navigation ────────────────────────────────────────────────────────
  // Handled by WeekPickerNav via setReviewWeekIdx

  // ── Day expand ─────────────────────────────────────────────────────────────
  const toggleDay = (date: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      next.has(date) ? next.delete(date) : next.add(date);
      return next;
    });
  };

  // ── Time-off actions ───────────────────────────────────────────────────────
  const updateDetailMapStatus = (id: number, status: TimeOffRequestStatus) => {
    setTimeOffDetailMap((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((date) => {
        next[date] = next[date].map((e) =>
          e.requestID === id ? { ...e, status } : e
        );
      });
      return next;
    });
  };

  // Unpaid Time (TAS-586): a 422 PAID_NOT_EXHAUSTED offers "Approve anyway" for this one request,
  // re-sent with the override; any other refusal shows the API's reason when it gave one.
  const handleApproveTimeOff = async (id: number, overrideExhausted = false) => {
    try {
      await approveTimeOffRequest(id, undefined, overrideExhausted ? { overrideExhausted: true } : undefined);
      setTimeOffRequests((prev) => prev.map((r) => r.timeOffRequestID === id ? { ...r, status: 'APPROVED' } : r));
      updateDetailMapStatus(id, 'APPROVED');
    } catch (err) {
      if (!overrideExhausted && isPaidNotExhaustedError(err)) {
        setCMConfig({
          variant: 'warning',
          icon: <AlertCircle size={22} />,
          title: 'Paid time isn’t used up',
          subtitle: 'This policy asks for paid time off to be used before unpaid time.',
          detail: <PaidOverrideDetail error={err} />,
          confirmLabel: 'Approve anyway',
          onConfirm: async () => {
            setCMConfig(null);
            await handleApproveTimeOff(id, true);
          },
        });
        return;
      }
      showError('Failed to approve time-off request.', refusalMessageOf(err, 'Please try again or contact support.'));
    }
  };

  const handleDenyTimeOff = async (id: number) => {
    try {
      await denyTimeOffRequest(id);
      setTimeOffRequests((prev) => prev.map((r) => r.timeOffRequestID === id ? { ...r, status: 'DENIED' } : r));
      updateDetailMapStatus(id, 'DENIED');
    } catch { showError('Failed to deny time-off request.', 'Please try again or contact support.'); }
  };

  // ── Send Back ──────────────────────────────────────────────────────────────
  const handleSendBack = (timesheetID: number) => {
    const empName = timesheetData
      ? `${timesheetData.name.first} ${timesheetData.name.last}${timesheetData.altEmployeeID ? ` · #${timesheetData.altEmployeeID}` : ''}`
      : undefined;
    const weekLabel = currentPayPeriod
      ? (() => {
          const fmt = (d: Date) => { const u = new Date(d); return `${u.getUTCMonth()+1}/${u.getUTCDate()}/${u.getUTCFullYear()}`; };
          return `Week ${currentPayPeriod.WeekNumber} · ${fmt(currentPayPeriod.StartDate)} – ${fmt(currentPayPeriod.EndDate)}`;
        })()
      : undefined;
    setCMConfig({
      variant: 'warning',
      icon: <Unlock size={22} />,
      title: 'Send timesheet back?',
      subtitle: 'The timesheet will be unlocked and returned to the employee for corrections.',
      detail: empName || weekLabel ? (
        <div>
          {empName && <strong>{empName}</strong>}
          {weekLabel && <div className="cm-detail-sub">{weekLabel}</div>}
        </div>
      ) : undefined,
      confirmLabel: 'Send Back',
      onConfirm: async () => {
        await unlockTimesheet(timesheetID, userID);
        setCMConfig({
          variant: 'success',
          icon: <CheckCircle size={22} />,
          title: 'Timesheet sent back',
          subtitle: 'The employee can now make corrections and resubmit.',
          onDismiss: handleBackToEmployees,
        });
      },
    });
  };

  // ── Approve to Payroll ─────────────────────────────────────────────────────
  const handleApproveToPayroll = async () => {
    if (!timesheetData) return;
    const days = timesheetData.days;
    const hasSickClaims = days.some((d) => d.employeeSickClaims?.length > 0);
    const hasPieceWork = days.some((d) => (d.totalClaimedAmount ?? 0) > 0);
    if (hasSickClaims && hasPieceWork) {
      setSickRateModalOpen(true);
    } else {
      await doApproveToPayroll(timesheetData.timesheetID);
    }
  };

  const doApproveToPayroll = async (timesheetID: number, hourlyRate?: number) => {
    setApproveLoading(true);
    try {
      if (hourlyRate !== undefined && timesheetData) {
        const allIDs = timesheetData.days.flatMap((d) =>
          (d.employeeSickClaims ?? []).map((sc: EmployeeSickClaim) => sc.employeeSickClaimID)
        );
        if (allIDs.length > 0) await updateSickClaimAmount(allIDs, hourlyRate);
      }
      await approveToPayroll(timesheetID, userID);
      setSickRateModalOpen(false);
      setCMConfig({
        variant: 'success',
        icon: <CheckCircle size={22} />,
        title: 'Approved to payroll',
        subtitle: 'The timesheet has been approved and sent to payroll.',
        onDismiss: handleBackToEmployees,
      });
    } catch {
      showError('Approval failed', 'Could not approve the timesheet. Please try again.');
    }
    finally { setApproveLoading(false); }
  };

  const handleSickRateConfirm = async () => {
    const rate = parseFloat(sickRateInput);
    if (isNaN(rate) || rate <= 0) { showError('Invalid hourly rate', 'Please enter a valid rate greater than zero.'); return; }
    if (!timesheetData) return;
    await doApproveToPayroll(timesheetData.timesheetID, rate);
  };

  // ── Derived data ───────────────────────────────────────────────────────────

  const totalCountForStatus = (key: StatusKey) =>
    (allSupervisorData[key] ?? []).reduce((sum, s) => sum + (s.employees?.length ?? s.count ?? 0), 0);

  // Supervisor-level has-alerts: true if any employee alert loaded as true
  const supervisorHasAlerts = (sup: Supervisor) =>
    (sup.employees ?? []).some((e) => employeeAlerts[e.employeeID]?.hasAlerts);
  const supervisorHasPendingTimeOff = (sup: Supervisor) =>
    (sup.employees ?? []).some((e) => employeeAlerts[e.employeeID]?.hasPendingTimeOff);

  // ──────────────────────────────────────────────────────────────────────────
  // ── Week navigation widget (shared across all views)
  // ──────────────────────────────────────────────────────────────────────────

  const weekNav = (
    <WeekPickerNav payPeriods={payPeriods} selectedIdx={reviewWeekIdx} onChange={setReviewWeekIdx} />
  );

  // ──────────────────────────────────────────────────────────────────────────
  // ── Breadcrumb
  // ──────────────────────────────────────────────────────────────────────────

  const breadcrumb = (
    <div className="tr2-breadcrumb">
      {view !== 'buckets' && (
        <>
          <span className="tr2-breadcrumb-item" onClick={handleBackToBuckets}>Buckets</span>
          <span className="tr2-breadcrumb-sep">›</span>
        </>
      )}
      {(view === 'supervisors' || view === 'employees' || view === 'review') && selectedStatus && (
        <>
          {view === 'supervisors' ? (
            <span className="tr2-breadcrumb-current">{BUCKET_LABELS[selectedStatus]}</span>
          ) : (
            <>
              <span className="tr2-breadcrumb-item" onClick={() => setView('supervisors')}>
                {BUCKET_LABELS[selectedStatus]}
              </span>
              <span className="tr2-breadcrumb-sep">›</span>
            </>
          )}
        </>
      )}
      {(view === 'employees' || view === 'review') && selectedSupervisor && (
        <>
          {view === 'employees' ? (
            <span className="tr2-breadcrumb-current">
              {selectedSupervisor.name?.last}, {selectedSupervisor.name?.first}
            </span>
          ) : (
            <>
              <span className="tr2-breadcrumb-item" onClick={handleBackToEmployees}>
                {selectedSupervisor.name?.last}, {selectedSupervisor.name?.first}
              </span>
              <span className="tr2-breadcrumb-sep">›</span>
            </>
          )}
        </>
      )}
      {view === 'review' && selectedEmployee && (
        <span className="tr2-breadcrumb-current">
          {selectedEmployee.name?.last}, {selectedEmployee.name?.first}
        </span>
      )}
    </div>
  );

  // ──────────────────────────────────────────────────────────────────────────
  // ── RENDER
  // ──────────────────────────────────────────────────────────────────────────

  return (
    <PageWrapper title="Timesheet Review" backgroundColor={Colors.White}>
      {view !== 'review' && weekNav}
      {breadcrumb}

      {/* ── BUCKET SCREEN ─────────────────────────────────────────────────── */}
      {view === 'buckets' && (
        <>
          {bucketsLoading && <div className="tr2-loading">Loading timesheet data…</div>}
          {bucketsError && <div className="tr2-error">{bucketsError}</div>}
          {!bucketsLoading && (
            <div className="tr2-buckets-grid">
              {STATUS_KEYS.map((key) => (
                <div
                  key={key}
                  className="tr2-bucket-card"
                  onClick={() => handleSelectBucket(key)}
                >
                  <div className="tr2-bucket-card-title">{BUCKET_LABELS[key]}</div>
                  <div className="tr2-bucket-card-count">{totalCountForStatus(key)}</div>
                  <div className="tr2-bucket-card-sub">
                    {(allSupervisorData[key] ?? []).length} supervisor
                    {(allSupervisorData[key] ?? []).length !== 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── SUPERVISOR LIST ───────────────────────────────────────────────── */}
      {view === 'supervisors' && selectedStatus && (
        <>
          <div className="tr2-list-section-title">
            {BUCKET_LABELS[selectedStatus]}
            <span style={{ fontWeight: 400, fontSize: 14, color: '#888', marginLeft: 10 }}>
              {totalCountForStatus(selectedStatus)} employee{totalCountForStatus(selectedStatus) !== 1 ? 's' : ''}
            </span>
          </div>
          {(allSupervisorData[selectedStatus] ?? []).length === 0 ? (
            <div className="tr2-empty">No supervisors with timesheets in this status.</div>
          ) : (
            (allSupervisorData[selectedStatus] ?? []).map((sup) => (
              <div
                key={sup.employeeID}
                className="tr2-supervisor-card"
                onClick={() => handleSelectSupervisor(sup)}
              >
                <div className="tr2-supervisor-info">
                  <div>
                    <div className="tr2-supervisor-name">
                      {sup.name?.last ?? ''}, {sup.name?.first ?? ''}
                    </div>
                    {sup.altEmployeeID && (
                      <div className="tr2-supervisor-alt">{sup.altEmployeeID}</div>
                    )}
                  </div>
                </div>
                <div className="tr2-supervisor-right">
                  <span className="tr2-count-badge">
                    {sup.employees?.length ?? sup.count ?? 0} emp
                  </span>
                  <span style={{ color: '#aaa', fontSize: 12 }}>▶</span>
                </div>
              </div>
            ))
          )}
        </>
      )}

      {/* ── EMPLOYEE LIST ─────────────────────────────────────────────────── */}
      {view === 'employees' && selectedSupervisor && (
        <>
          <div className="tr2-list-section-title">
            {selectedSupervisor.name?.last}, {selectedSupervisor.name?.first}
            <span style={{ fontWeight: 400, fontSize: 14, color: '#888', marginLeft: 10 }}>
              {(selectedSupervisor.employees ?? []).length} employee
              {(selectedSupervisor.employees ?? []).length !== 1 ? 's' : ''}
            </span>
          </div>
          {(selectedSupervisor.employees ?? []).length === 0 ? (
            <div className="tr2-empty">No employees in this supervisor's list.</div>
          ) : (
            <div className="tr2-employee-list">
              {(selectedSupervisor.employees ?? []).map((emp) => {
                const alertInfo = employeeAlerts[emp.employeeID];
                return (
                  <div
                    key={emp.employeeID}
                    className="tr2-employee-card"
                    onClick={() => handleSelectEmployee(emp)}
                  >
                    <div className="tr2-employee-card-left">
                      <div>
                        <div className="tr2-employee-name">
                          {emp.name?.last ?? ''}, {emp.name?.first ?? ''}
                        </div>
                        {emp.altEmployeeID && (
                          <div className="tr2-employee-altid">{emp.altEmployeeID}</div>
                        )}
                      </div>
                    </div>
                    <div className="tr2-employee-indicators">
                      {alertInfo?.loading && <span className="tr2-loading-alerts" title="Loading…" />}
                      {!alertInfo?.loading && alertInfo?.hasAlerts && (
                        <span className="tr2-alert-badge" title="Has alerts">!</span>
                      )}
                      {!alertInfo?.loading && alertInfo?.hasPendingTimeOff && (
                        <span className="tr2-timeoff-badge" title="Pending time off">Time Off</span>
                      )}
                      <span style={{ color: '#aaa', fontSize: 12 }}>▶</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── EMPLOYEE REVIEW ───────────────────────────────────────────────── */}
      {view === 'review' && (
        <div className="tr2-review-wrap">
          {/* Initial load (no prior data yet) */}
          {reviewLoading && !timesheetData && <div className="tr2-loading">Loading timesheet…</div>}
          {reviewError && <div className="tr2-error">{reviewError}</div>}

          {timesheetData && (
            <EmployeeTimesheetReviewConsole
              timesheetData={timesheetData}
              driveTimeEntries={driveTimeEntries}
              timeOffRequests={timeOffRequests}
              timeOffDetailMap={timeOffDetailMap}
              holidays={holidays}
              selectedStatus={actualStatus ?? selectedStatus}
              payPeriods={payPeriods}
              reviewWeekIdx={reviewWeekIdx}
              onSelectWeek={setReviewWeekIdx}
              supervisorName={
                selectedSupervisor
                  ? `${selectedSupervisor.name?.first ?? ''} ${selectedSupervisor.name?.last ?? ''}`.trim()
                  : undefined
              }
              approveLoading={approveLoading}
              reviewLoading={reviewLoading}
              noTimesheetForWeek={noTimesheetForWeek}
              onApproveTimeOff={handleApproveTimeOff}
              onDenyTimeOff={handleDenyTimeOff}
              onSendBack={handleSendBack}
              onApproveWeek={handleApproveToPayroll}
              onRefresh={() => {
                if (selectedEmployee && selectedStatus)
                  loadReview(selectedEmployee, selectedStatus, currentPayPeriodID);
              }}
              canEditMiscTaskCodePerm={
                can(userRole, 'editMiscTaskAccountCodeAndDescription') && !config.combineTasksInWeek
              }
            />
          )}
        </div>
      )}

      {/* ── Sick Rate Modal ────────────────────────────────────────────────── */}
      {sickRateModalOpen && (
        <div className="tr2-modal-overlay">
          <div className="tr2-modal">
            <h3>Enter Sick Hourly Rate</h3>
            <p>
              This employee has sick time claims and piece work this week. Enter the hourly rate
              to apply before approving.
            </p>
            <input
              type="number"
              placeholder="Hourly rate (e.g. 18.50)"
              value={sickRateInput}
              onChange={(e) => setSickRateInput(e.target.value)}
              min="0"
              step="0.01"
            />
            <div className="tr2-modal-actions">
              <button className="tr2-btn" onClick={() => setSickRateModalOpen(false)} disabled={approveLoading}>
                Cancel
              </button>
              <button className="tr2-btn primary" onClick={handleSickRateConfirm} disabled={approveLoading}>
                {approveLoading ? 'Approving…' : 'Confirm & Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm / Result Modal */}
      {cmConfig && (
        <ConfirmModal
          {...cmConfig}
          onClose={dismissCM}
        />
      )}
    </PageWrapper>
  );
};

// ── ReviewPanel — self-contained employee review sub-component ─────────────────

interface ReviewPanelProps {
  timesheetData: Employee;
  driveTimeEntries: DriveTimeEntry[];
  timeOffRequests: TimeOffRequestSummary[];
  timeOffDetailMap: Record<string, TimeOffDayEntry[]>;
  holidays: HolidayCalendarEntry[];
  expandedDays: Set<string>;
  selectedStatus: StatusKey | null;
  currentPayPeriod: PayPeriod | undefined;
  userID: number;
  approveLoading: boolean;
  onToggleDay: (date: string) => void;
  onApproveTimeOff: (id: number) => void;
  onDenyTimeOff: (id: number) => void;
  onSendBack: (timesheetID: number) => void;
  onApproveToPayroll: () => void;
  onRefresh: () => void;
}

const ReviewPanel: React.FC<ReviewPanelProps> = ({
  timesheetData,
  driveTimeEntries,
  timeOffRequests,
  timeOffDetailMap,
  holidays,
  expandedDays,
  selectedStatus,
  currentPayPeriod,
  approveLoading,
  onToggleDay,
  onApproveTimeOff,
  onDenyTimeOff,
  onSendBack,
  onApproveToPayroll,
  onRefresh,
}) => {
  const days = timesheetData.days ?? [];
  const weekAlerts = computeWeekAlerts(days);
  const totalHoursMin = days.reduce((s, d) => s + (d.totalWorkMinutes ?? 0), 0);
  const totalPiece = days.reduce((s, d) => s + (d.totalClaimedAmount ?? 0), 0);
  const totalOTMin = days.reduce((s, d) => s + (d.overTime ?? 0), 0);
  const totalTimeOffMin = days.reduce(
    (s, d) => s + (d.employeeSickClaims ?? []).reduce((ss: number, sc: EmployeeSickClaim) => ss + (sc.minutesClaimed ?? 0), 0),
    0
  );

  // ── Holiday eligibility ──────────────────────────────────────────────────────
  const payPeriodStart = currentPayPeriod ? isoDateOnly(new Date(currentPayPeriod.StartDate)) : '';
  const payPeriodEnd = currentPayPeriod ? isoDateOnly(new Date(currentPayPeriod.EndDate)) : '';

  const holidayEligMap: Record<number, HolidayEligibility> = {};
  holidays.forEach((h) => {
    holidayEligMap[h.holidayCalendarID] = checkHolidayEligibility(
      h, days, timeOffDetailMap, payPeriodStart, payPeriodEnd
    );
  });

  const holidayFailed = (e: HolidayEligibility | undefined): boolean =>
    !!e && (e.tenureStatus === 'fail' || e.beforeStatus === 'fail' || e.afterStatus === 'fail');

  const ineligibleHolidays = holidays.filter((h) => holidayFailed(holidayEligMap[h.holidayCalendarID]));

  const totalHolidayHoursEligible = holidays.reduce((sum, h) => {
    if (holidayFailed(holidayEligMap[h.holidayCalendarID])) return sum;
    return sum + (h.paidHoursIfNotWorked ?? 0) / 60;
  }, 0);

  const totalAlerts = weekAlerts.missingPunch + weekAlerts.mealViolation + weekAlerts.editedTime + ineligibleHolidays.length;

  const hasPendingTimeOff = timeOffRequests.some((r) => r.status === 'PENDING');
  const canApprove =
    selectedStatus === 'submitted' || selectedStatus === 'supervisorApproved';

  return (
    <>
      {/* EE Header Card */}
      <div className="tr2-review-header-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>
              {timesheetData.name?.last ?? ''}, {timesheetData.name?.first ?? ''}
              {timesheetData.altEmployeeID ? ` – ${timesheetData.altEmployeeID}` : ''}
            </div>
            <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
              Week {getWeekLabel(currentPayPeriod)}
            </div>
          </div>
          <button className="tr2-btn" onClick={onRefresh}>Refresh</button>
        </div>

        <div className="tr2-stat-row">
          <div className="tr2-stat">
            <span className="tr2-stat-label">⏱ Hours</span>
            <span className="tr2-stat-value">{formatDecimalHours(totalHoursMin)}</span>
          </div>
          <div className="tr2-stat">
            <span className="tr2-stat-label">🔥 Piece $</span>
            <span className="tr2-stat-value">{formatDollar(totalPiece)}</span>
          </div>
          <div className="tr2-stat">
            <span className="tr2-stat-label">OT</span>
            <span className="tr2-stat-value">{formatDecimalHours(totalOTMin)}</span>
          </div>
        </div>

        {totalAlerts > 0 && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#888', marginBottom: 6 }}>
              ⚠️ Alerts
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {weekAlerts.missingPunch > 0 && (
                <span style={{ color: '#c0392b', fontWeight: 600, fontSize: 13 }}>
                  🔴 Missing Punch: {weekAlerts.missingPunch}
                </span>
              )}
              {weekAlerts.mealViolation > 0 && (
                <span style={{ color: '#c0392b', fontWeight: 600, fontSize: 13 }}>
                  🔴 Meal Violation: {weekAlerts.mealViolation}
                </span>
              )}
              {weekAlerts.editedTime > 0 && (
                <span style={{ color: '#e67e22', fontWeight: 600, fontSize: 13 }}>
                  🟡 Edited: {weekAlerts.editedTime}
                </span>
              )}
              {ineligibleHolidays.length > 0 && (
                <span style={{ color: '#c0392b', fontWeight: 600, fontSize: 13 }}>
                  🔴 Holiday Ineligible: {ineligibleHolidays.length}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Attention Banner */}
      {totalAlerts > 0 && (
        <div className="tr2-alert-banner">
          <div className="tr2-alert-banner-title">⚠️ ATTENTION REQUIRED</div>
          <div className="tr2-alert-banner-items">
            {weekAlerts.missingPunch > 0 && <span>• {weekAlerts.missingPunch} Missing Punch</span>}
            {weekAlerts.mealViolation > 0 && <span>• {weekAlerts.mealViolation} Meal Violation</span>}
            {weekAlerts.editedTime > 0 && <span>• {weekAlerts.editedTime} Edited</span>}
            {ineligibleHolidays.length > 0 && <span>• {ineligibleHolidays.length} Holiday Ineligible</span>}
          </div>
        </div>
      )}

      {/* Holiday Ineligibility Banner */}
      {ineligibleHolidays.length > 0 && (
        <div className="tr2-holiday-ineligible-banner">
          <div className="tr2-holiday-ineligible-banner-title">🎉 ⚠️ HOLIDAY PAY ELIGIBILITY ISSUE</div>
          {ineligibleHolidays.map((h) => {
            const e = holidayEligMap[h.holidayCalendarID];
            return (
              <div key={h.holidayCalendarID} className="tr2-holiday-ineligible-item">
                <strong>{h.name}</strong> ({h.holidayDate})
                {e.tenureStatus === 'fail' && (
                  <span>
                    {' · '}
                    {h.eligibility?.tenure?.eligibilityDays != null
                      ? `Hired less than ${h.eligibility.tenure.eligibilityDays} days before the holiday`
                      : 'Hired too recently before the holiday'}
                    {h.eligibility?.tenure?.eligibleOn
                      ? ` (eligible from ${fmtLongDate(h.eligibility.tenure.eligibleOn)})`
                      : ''}
                  </span>
                )}
                {e.beforeStatus === 'fail' && (
                  <span> · Did not work the required day before ({e.beforeDate})</span>
                )}
                {e.afterStatus === 'fail' && (
                  <span> · Did not work the required day after ({e.afterDate})</span>
                )}
              </div>
            );
          })}
          <div className="tr2-holiday-ineligible-note">
            Employee may not be eligible for holiday pay. Approved time off counts as "worked."
          </div>
        </div>
      )}

      {/* Week Totals */}
      <div className="tr2-totals-row">
        {[
          { label: 'Total Hours', value: formatDecimalHours(totalHoursMin) },
          { label: 'Piece $', value: formatDollar(totalPiece) },
          { label: 'OT Hours', value: formatDecimalHours(totalOTMin) },
          { label: 'Time Off', value: formatDecimalHours(totalTimeOffMin) },
        ].map(({ label, value }) => (
          <div key={label} className="tr2-total-card">
            <div className="tr2-stat-label">{label}</div>
            <div className="tr2-stat-value">{value}</div>
          </div>
        ))}
        {holidays.length > 0 && (
          <div className={`tr2-total-card ${ineligibleHolidays.length > 0 ? 'tr2-total-card-alert' : ''}`}>
            <div className="tr2-stat-label">Holiday</div>
            <div className="tr2-stat-value" style={ineligibleHolidays.length > 0 ? { color: '#c0392b' } : {}}>
              {ineligibleHolidays.length > 0 ? '0.0' : totalHolidayHoursEligible.toFixed(1)}
            </div>
            {ineligibleHolidays.length > 0 && (
              <div style={{ fontSize: 10, color: '#c0392b', marginTop: 2 }}>⚠️ Ineligible</div>
            )}
          </div>
        )}
      </div>

      {/* Day Grid */}
      <div className="tr2-day-grid">
        <div className="tr2-day-grid-header">
          <span>Day</span><span>Hours</span><span>Piece $</span>
          <span>OT</span><span>Meal</span><span>Status</span><span />
        </div>
        {days.map((day) => {
          const alerts = computeDayAlerts(day);
          const expanded = expandedDays.has(day.date);
          const gapSet = getTimeGapEntries(day);
          const mealEntries = day.timeEntries.filter((e) => e.type === TimeEntryType.Meal);
          const mealText = day.noLunchException ? '✗' : mealEntries.length > 0 ? '✓' : '—';
          const dayDate = day.date.substring(0, 10);

          let statusText = '✅ Clean';
          let statusClass = 'clean';
          if (alerts.missingPunch) { statusText = '🔴 Missing Punch'; statusClass = 'alert'; }
          else if (alerts.mealViolation) { statusText = '🔴 Meal Violation'; statusClass = 'alert'; }
          else if (alerts.editedTime) { statusText = '🟡 Edited'; statusClass = 'warn'; }

          const dayDrive = driveTimeEntries.filter(
            (dt) => dt.workDate && dt.workDate.startsWith(dayDate)
          );
          const dayTOEntries = timeOffDetailMap[dayDate] ?? [];
          const dayHoliday = holidays.find((h) => h.holidayDate === dayDate) ?? null;
          const dayHolidayEligibility = dayHoliday ? holidayEligMap[dayHoliday.holidayCalendarID] : null;
          const dayHolidayIneligible = holidayFailed(dayHolidayEligibility ?? undefined);

          return (
            <div key={day.date} className="tr2-day-row">
              <div className="tr2-day-row-summary" onClick={() => onToggleDay(day.date)}>
                <span style={{ fontWeight: 600 }}>{getDayLabel(day.date)}</span>
                <span>{formatDecimalHours(day.totalWorkMinutes)}</span>
                <span>{day.totalClaimedAmount > 0 ? formatDollar(day.totalClaimedAmount) : '—'}</span>
                <span>{day.overTime > 0 ? formatDecimalHours(day.overTime) : '—'}</span>
                <span>{mealText}</span>
                <span className={`tr2-day-status ${statusClass}`}>
                  {statusText}
                  {dayTOEntries.length > 0 && (
                    <span className="tr2-day-badge to" title="Has time-off requests"> T.O.</span>
                  )}
                  {dayHoliday && (
                    <span
                      className={`tr2-day-badge hol${dayHolidayIneligible ? ' alert' : ''}`}
                      title={dayHoliday.name}
                    > HOL</span>
                  )}
                </span>
                <button className="tr2-expand-btn">{expanded ? '▲' : '▼'}</button>
              </div>
              {expanded && (
                <DayDetailPanel
                  day={day}
                  gapSet={gapSet}
                  dayDrive={dayDrive}
                  dayTimeOff={dayTOEntries}
                  dayHoliday={dayHoliday}
                  dayHolidayEligibility={dayHolidayEligibility}
                  timesheetID={timesheetData.timesheetID}
                  selectedStatus={selectedStatus}
                  onSendBack={onSendBack}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Time Off Requests */}
      {timeOffRequests.length > 0 && (
        <div className="tr2-timeoff-section">
          <div className="tr2-timeoff-section-title">📅 Time Off Requests</div>
          {timeOffRequests.map((req) => (
            <div key={req.timeOffRequestID} className="tr2-timeoff-request-row">
              <div className="tr2-timeoff-request-info">
                <span className={`tr2-status-badge ${req.status}`}>{req.status}</span>
                <span>{req.categoryName}</span>
                <span>
                  {getShortDate(req.startDate)}
                  {req.startDate !== req.endDate ? ` – ${getShortDate(req.endDate)}` : ''}
                </span>
                <span>{req.totalHours} hrs</span>
              </div>
              {req.status === 'PENDING' && (
                <div className="tr2-timeoff-actions">
                  <button className="tr2-btn" onClick={() => onApproveTimeOff(req.timeOffRequestID)}>
                    Approve
                  </button>
                  <button className="tr2-btn danger" onClick={() => onDenyTimeOff(req.timeOffRequestID)}>
                    Deny
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Approve to Payroll */}
      {canApprove && (
        <div className="tr2-approve-section">
          <button
            className="tr2-approve-btn"
            disabled={hasPendingTimeOff || approveLoading}
            onClick={onApproveToPayroll}
          >
            {approveLoading ? 'Approving…' : 'Approve → Payroll'}
          </button>
          {hasPendingTimeOff && (
            <span className="tr2-approve-warning">
              ⚠️ Resolve pending time-off requests before approving
            </span>
          )}
        </div>
      )}
    </>
  );
};

// ── DayDetailPanel sub-component ──────────────────────────────────────────────

interface DayDetailPanelProps {
  day: Day;
  gapSet: Set<string>;
  dayDrive: DriveTimeEntry[];
  dayTimeOff: TimeOffDayEntry[];
  dayHoliday: HolidayCalendarEntry | null;
  dayHolidayEligibility: HolidayEligibility | null;
  timesheetID: number;
  selectedStatus: StatusKey | null;
  onSendBack: (id: number) => void;
}

const DayDetailPanel: React.FC<DayDetailPanelProps> = ({
  day, gapSet, dayDrive, dayTimeOff, dayHoliday, dayHolidayEligibility,
  timesheetID, selectedStatus, onSendBack,
}) => {
  const regularEntries = day.timeEntries.filter((e) => e.type === TimeEntryType.Regular);
  const mealEntries = day.timeEntries.filter((e) => e.type === TimeEntryType.Meal);
  const alerts = computeDayAlerts(day);
  const hasIssues = alerts.missingPunch || alerts.mealViolation;
  const canSendBack = selectedStatus === 'submitted' || selectedStatus === 'supervisorApproved';

  return (
    <div className="tr2-day-detail">
      {/* TIME */}
      {regularEntries.length > 0 && (
        <div className="tr2-detail-section">
          <div className="tr2-detail-label">⏱ TIME</div>
          {regularEntries.map((entry, idx) => {
            const gapIn = gapSet.has(`te-${idx}-in`);
            const gapOut = gapSet.has(`te-${idx}-out`);
            const inTime = applyOffsetToUTC(entry.inDateTime, entry.inDateTimeOffset);
            const outTime = entry.outDateTime
              ? applyOffsetToUTC(entry.outDateTime, entry.outDateTimeOffset)
              : null;
            const durationMin = outTime
              ? (new Date(entry.outDateTime).getTime() - new Date(entry.inDateTime).getTime()) / 60000
              : null;
            return (
              <div key={entry.timeEntryID} className={`tr2-detail-row ${gapIn || gapOut ? 'gap-highlight' : ''}`}>
                {inTime} – {outTime ? (
                  <>
                    {outTime}
                    {durationMin !== null && (
                      <span style={{ color: '#888', marginLeft: 6 }}>({formatDecimalHours(durationMin)} hrs)</span>
                    )}
                  </>
                ) : (
                  <span className="tr2-missing-out">✗ Missing Out</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* MEAL */}
      {(mealEntries.length > 0 || day.noLunchException) && (
        <div className="tr2-detail-section">
          <div className="tr2-detail-label">🍽 MEAL</div>
          {mealEntries.map((entry) => {
            const inTime = applyOffsetToUTC(entry.inDateTime, entry.inDateTimeOffset);
            const outTime = entry.outDateTime ? applyOffsetToUTC(entry.outDateTime, entry.outDateTimeOffset) : null;
            const durationMin = outTime
              ? (new Date(entry.outDateTime).getTime() - new Date(entry.inDateTime).getTime()) / 60000
              : null;
            return (
              <div key={entry.timeEntryID} className="tr2-detail-row">
                {inTime} – {outTime ?? '—'}
                {durationMin !== null && <span style={{ color: '#888', marginLeft: 6 }}>({formatDecimalHours(durationMin)} hrs)</span>}
              </div>
            );
          })}
          {day.noLunchException && (
            <div className="tr2-detail-row" style={{ color: '#e67e22' }}>⚠️ Meal penalty applied</div>
          )}
        </div>
      )}

      {/* TIME OFF */}
      {dayTimeOff.length > 0 && (
        <div className="tr2-detail-section">
          <div className="tr2-detail-label">📅 TIME OFF</div>
          {dayTimeOff.map((entry, idx) => (
            <div key={idx} className="tr2-detail-row">
              <span className={`tr2-status-badge ${entry.status}`}>{entry.status}</span>
              {' '}{entry.category}
              {' – '}{entry.hours} hr{entry.hours !== 1 ? 's' : ''}
            </div>
          ))}
        </div>
      )}

      {/* HOLIDAY */}
      {dayHoliday && (
        <div className="tr2-detail-section">
          <div className="tr2-detail-label">🎉 HOLIDAY</div>
          <div className="tr2-detail-row">
            <strong>{dayHoliday.name}</strong>
            {' · '}{(dayHoliday.paidHoursIfNotWorked / 60).toFixed(1)} hrs paid
          </div>
          {dayHolidayEligibility && (
            <>
              {dayHolidayEligibility.tenureStatus !== 'na' && (
                <div className={`tr2-detail-row${dayHolidayEligibility.tenureStatus === 'fail' ? ' tr2-detail-row-alert' : ''}`}>
                  {dayHolidayEligibility.tenureStatus === 'pass' && '✅'}
                  {dayHolidayEligibility.tenureStatus === 'fail' && '🔴'}
                  {dayHolidayEligibility.tenureStatus === 'unknown' && '⚠️'}
                  {' '}{dayHolidayEligibility.tenureNote}
                  {dayHolidayEligibility.tenureStatus === 'fail' && ' — INELIGIBLE'}
                </div>
              )}
              {dayHolidayEligibility.beforeStatus !== 'na' && (
                <div className={`tr2-detail-row${dayHolidayEligibility.beforeStatus === 'fail' ? ' tr2-detail-row-alert' : ''}`}>
                  {dayHolidayEligibility.beforeStatus === 'pass' && '✅'}
                  {dayHolidayEligibility.beforeStatus === 'fail' && '🔴'}
                  {dayHolidayEligibility.beforeStatus === 'outside' && '⚠️'}
                  {' '}Day before ({dayHolidayEligibility.beforeDate}):{' '}
                  {dayHolidayEligibility.beforeStatus === 'pass' && `Worked ✓${dayHolidayEligibility.beforeNote}`}
                  {dayHolidayEligibility.beforeStatus === 'fail' && `Did not work${dayHolidayEligibility.beforeNote} — INELIGIBLE`}
                  {dayHolidayEligibility.beforeStatus === 'outside' && 'Could not check (outside period)'}
                </div>
              )}
              {dayHolidayEligibility.afterStatus !== 'na' && (
                <div className={`tr2-detail-row${dayHolidayEligibility.afterStatus === 'fail' ? ' tr2-detail-row-alert' : ''}`}>
                  {dayHolidayEligibility.afterStatus === 'pass' && '✅'}
                  {dayHolidayEligibility.afterStatus === 'fail' && '🔴'}
                  {dayHolidayEligibility.afterStatus === 'outside' && '⚠️'}
                  {' '}Day after ({dayHolidayEligibility.afterDate}):{' '}
                  {dayHolidayEligibility.afterStatus === 'pass' && `Worked ✓${dayHolidayEligibility.afterNote}`}
                  {dayHolidayEligibility.afterStatus === 'fail' && `Did not work${dayHolidayEligibility.afterNote} — INELIGIBLE`}
                  {dayHolidayEligibility.afterStatus === 'outside' && 'Could not check (outside period)'}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* DRIVE */}
      {dayDrive.length > 0 && (
        <div className="tr2-detail-section">
          <div className="tr2-detail-label">🚗 DRIVE</div>
          {dayDrive.map((dt) => (
            <div key={dt.driveTimeEntryID} className="tr2-detail-row">
              {dt.direction}: {dt.totalDriveMinutes} min
              {dt.excessMinutes > 0 && (
                <span style={{ color: '#e67e22', marginLeft: 6 }}>(excess: {dt.excessMinutes} min)</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* PIECE */}
      {day.projectEmployeeTasks?.length > 0 && (
        <div className="tr2-detail-section">
          <div className="tr2-detail-label">🔥 PIECE</div>
          {day.projectEmployeeTasks.map((pet) => {
            const taskCount = (pet.employeeTasks?.length ?? 0) + (pet.miscEmployeeTasks?.length ?? 0);
            return (
              <div key={pet.project.projectID}>
                <div className="tr2-detail-row">
                  {taskCount} task{taskCount !== 1 ? 's' : ''} · {formatDollar(pet.totalAmountClaimed)}
                  <span style={{ color: '#888', marginLeft: 6 }}>({pet.project.name})</span>
                </div>
                {pet.employeeTasks?.map((et) => (
                  <div key={et.employeeTaskID} className="tr2-piece-task-row">
                    → {et.task.taskInfo.name}: {formatDollar(et.amountClaimed)}
                    {et.task.originalAmount > 0 && (
                      <span style={{ color: '#aaa', marginLeft: 4 }}>
                        · {((et.amountClaimed / et.task.originalAmount) * 100).toFixed(1)}% of {formatDollar(et.task.originalAmount)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* JOB */}
      {day.hourlyProjects?.length > 0 && (
        <div className="tr2-detail-section">
          <div className="tr2-detail-label">📍 JOB</div>
          {day.hourlyProjects.map((hp, idx) => (
            <div key={idx} className="tr2-detail-row">
              {hp.project.name}
              {hp.account ? ` · Cost: ${hp.account.altAccountCodeID}` : ''}
            </div>
          ))}
        </div>
      )}

      {/* Issues + Send Back */}
      {hasIssues && (
        <div className="tr2-detail-section">
          {alerts.missingPunch && <div className="tr2-issue-row">⚠️ ISSUE: Missing clock-out time</div>}
          {alerts.mealViolation && <div className="tr2-issue-row">⚠️ ISSUE: Meal violation detected</div>}
          {canSendBack && (
            <div style={{ marginTop: 8 }}>
              <button className="tr2-btn danger" onClick={() => onSendBack(timesheetID)}>
                Send Back
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimesheetReview2Page;
